// src/admin/AdminCoupons.js
import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, doc, updateDoc,
  deleteDoc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  Tag, Plus, Edit2, Trash2, ToggleLeft, ToggleRight,
  X, Save, Copy, CheckCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY = {
  code:          '',
  type:          'percent',   // 'percent' | 'flat'
  value:         '',
  minOrder:      '',
  maxUses:       '',
  perUser:       1,
  autoApply:     false,
  newUserOnly:   false,
  description:   '',
  expiresAt:     '',
  isActive:      true,
};

// ── Form inside a modal ─────────────────────────────────────────
function CouponForm({ initial, onSave, onClose }) {
  const [form,   setForm]   = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);

  const change = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code.trim())  { toast.error('Coupon code required');  return; }
    if (!form.value)        { toast.error('Discount value required'); return; }
    if (form.type === 'percent' && Number(form.value) > 100) {
      toast.error('Percent discount cannot exceed 100%'); return;
    }

    setSaving(true);
    try {
      const payload = {
        code:        form.code.trim().toUpperCase(),
        type:        form.type,
        value:       Number(form.value),
        minOrder:    form.minOrder   ? Number(form.minOrder)  : 0,
        maxUses:     form.maxUses    ? Number(form.maxUses)   : null,
        perUser:     form.perUser    ? Number(form.perUser)   : 1,
        autoApply:   form.autoApply,
        newUserOnly: form.newUserOnly,
        description: form.description.trim(),
        expiresAt:   form.expiresAt  ? new Date(form.expiresAt) : null,
        isActive:    form.isActive,
        usedCount:   initial?.usedCount || 0,
      };
      await onSave(payload);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-gray-800 text-sm bg-gray-50 focus:bg-white transition-all";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h3 className="font-display font-bold text-gray-900 text-lg">
            {initial ? 'Edit Coupon' : 'Create New Coupon'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-4">
          {/* Code */}
          <div>
            <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
              Coupon Code *
            </label>
            <input
              type="text"
              value={form.code}
              onChange={change('code')}
              placeholder="e.g. WELCOME10, LOVE20, FIRST50"
              className={`${inputCls} font-mono uppercase`}
              autoComplete="off"
            />
            <p className="text-xs text-gray-400 font-body mt-1">
              Customers will enter this at checkout. Use uppercase letters.
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
              Description
            </label>
            <input
              type="text"
              value={form.description}
              onChange={change('description')}
              placeholder="e.g. 10% off for new users"
              className={inputCls}
            />
          </div>

          {/* Type + Value */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                Discount Type *
              </label>
              <select value={form.type} onChange={change('type')} className={inputCls}>
                <option value="percent">Percentage (%) off</option>
                <option value="flat">Flat (₹) off</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                {form.type === 'percent' ? 'Discount %' : 'Discount ₹'} *
              </label>
              <input
                type="number"
                value={form.value}
                onChange={change('value')}
                placeholder={form.type === 'percent' ? '10' : '100'}
                min="1"
                max={form.type === 'percent' ? 100 : undefined}
                className={inputCls}
              />
            </div>
          </div>

          {/* Min order + Max uses */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                Min Order Amount (₹)
              </label>
              <input
                type="number"
                value={form.minOrder}
                onChange={change('minOrder')}
                placeholder="0 = no minimum"
                min="0"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                Max Total Uses
              </label>
              <input
                type="number"
                value={form.maxUses}
                onChange={change('maxUses')}
                placeholder="Leave blank = unlimited"
                min="1"
                className={inputCls}
              />
            </div>
          </div>

          {/* Per user + Expiry */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                Uses Per User
              </label>
              <input
                type="number"
                value={form.perUser}
                onChange={change('perUser')}
                placeholder="1"
                min="1"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">
                Expiry Date
              </label>
              <input
                type="date"
                value={form.expiresAt}
                onChange={change('expiresAt')}
                min={new Date().toISOString().split('T')[0]}
                className={inputCls}
              />
            </div>
          </div>

          {/* Flags */}
          <div className="bg-gray-50 rounded-2xl p-4 space-y-3">
            <p className="text-xs font-body font-bold text-gray-500 uppercase tracking-wider">Coupon Flags</p>
            {[
              { field: 'newUserOnly', label: '🆕 New users only',    sub: 'Only applies to first-time buyers'      },
              { field: 'autoApply',  label: '⚡ Auto-apply',         sub: 'Applied automatically at checkout'      },
              { field: 'isActive',   label: '✅ Active',             sub: 'Coupon is live and usable'              },
            ].map(({ field, label, sub }) => (
              <label key={field} className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="font-body font-medium text-gray-800 text-sm">{label}</p>
                  <p className="font-body text-xs text-gray-400">{sub}</p>
                </div>
                <div
                  onClick={() => setForm((f) => ({ ...f, [field]: !f[field] }))}
                  className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${form[field] ? 'bg-rose-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${form[field] ? 'left-5' : 'left-0.5'}`} />
                </div>
              </label>
            ))}
          </div>

          {/* Preview */}
          {form.code && form.value && (
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">
              <p className="text-xs font-body font-bold text-rose-500 uppercase tracking-wider mb-1">Preview</p>
              <p className="font-mono font-bold text-rose-700 text-lg">{form.code.toUpperCase()}</p>
              <p className="text-gray-600 font-body text-sm mt-1">
                {form.type === 'percent' ? `${form.value}% off` : `₹${form.value} off`}
                {form.minOrder ? ` on orders above ₹${form.minOrder}` : ''}
                {form.newUserOnly ? ' · New users only' : ''}
                {form.autoApply ? ' · Auto-applied' : ''}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-3.5 rounded-2xl font-body font-semibold transition-all disabled:opacity-60">
              {saving
                ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                : <Save size={16} />
              }
              {saving ? 'Saving...' : initial ? 'Update Coupon' : 'Create Coupon'}
            </button>
            <button type="button" onClick={onClose}
              className="px-6 py-3.5 rounded-2xl border border-gray-200 text-gray-600 hover:bg-gray-50 font-body font-medium transition-all">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Coupon Row ──────────────────────────────────────────────────
function CouponRow({ coupon, onEdit, onToggle, onDelete }) {
  const [copied,   setCopied]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const expired = coupon.expiresAt && new Date(coupon.expiresAt?.toDate?.() || coupon.expiresAt) < new Date();

  const copyCode = () => {
    navigator.clipboard.writeText(coupon.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete coupon "${coupon.code}"?`)) return;
    setDeleting(true);
    try { await onDelete(coupon.id); }
    finally { setDeleting(false); }
  };

  return (
    <div className={`bg-white rounded-2xl border shadow-sm p-4 flex flex-wrap items-center gap-4 ${
      !coupon.isActive || expired ? 'border-gray-200 opacity-60' : 'border-gray-100'
    }`}>
      {/* Code */}
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-mono font-black text-rose-600 text-lg tracking-wider">{coupon.code}</span>
        <button onClick={copyCode}
          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-rose-50 transition-colors">
          {copied ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} className="text-gray-400" />}
        </button>
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap gap-1.5 mb-1">
          <span className="text-xs bg-rose-100 text-rose-700 font-bold px-2.5 py-1 rounded-full">
            {coupon.type === 'percent' ? `${coupon.value}% off` : `₹${coupon.value} off`}
          </span>
          {coupon.minOrder > 0 && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-body">
              Min ₹{coupon.minOrder}
            </span>
          )}
          {coupon.newUserOnly && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-body font-semibold">
              🆕 New Users
            </span>
          )}
          {coupon.autoApply && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full font-body font-semibold">
              ⚡ Auto
            </span>
          )}
          {expired && (
            <span className="text-xs bg-red-100 text-red-600 px-2.5 py-1 rounded-full font-body font-semibold">
              Expired
            </span>
          )}
        </div>
        {coupon.description && (
          <p className="text-xs text-gray-400 font-body">{coupon.description}</p>
        )}
        <p className="text-xs text-gray-400 font-body mt-0.5">
          Used {coupon.usedCount || 0} times
          {coupon.maxUses ? ` / ${coupon.maxUses} max` : ''}
          {coupon.expiresAt && ` · Expires ${new Date(coupon.expiresAt?.toDate?.() || coupon.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => onToggle(coupon)}
          className="flex items-center gap-1.5 text-xs font-body font-semibold transition-colors">
          {coupon.isActive
            ? <ToggleRight size={22} className="text-green-500" />
            : <ToggleLeft  size={22} className="text-gray-400" />
          }
        </button>
        <button onClick={() => onEdit(coupon)}
          className="w-8 h-8 bg-blue-50 hover:bg-blue-500 text-blue-500 hover:text-white rounded-xl flex items-center justify-center transition-all">
          <Edit2 size={14} />
        </button>
        <button onClick={handleDelete} disabled={deleting}
          className="w-8 h-8 bg-red-50 hover:bg-red-500 text-red-500 hover:text-white rounded-xl flex items-center justify-center transition-all disabled:opacity-50">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────
export default function AdminCoupons() {
  const [coupons,   setCoupons]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [editCoupon,setEditCoupon] = useState(null);

  const fetchCoupons = async () => {
    try {
      const snap = await getDocs(collection(db, 'coupons'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      setCoupons(list);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const handleCreate = async (data) => {
    const ref = await addDoc(collection(db, 'coupons'), {
      ...data, usedCount: 0, createdAt: serverTimestamp(),
    });
    setCoupons((prev) => [{ id: ref.id, ...data, usedCount: 0 }, ...prev]);
    toast.success(`Coupon "${data.code}" created! ✅`);
  };

  const handleEdit = async (data) => {
    await updateDoc(doc(db, 'coupons', editCoupon.id), data);
    setCoupons((prev) => prev.map((c) => c.id === editCoupon.id ? { ...c, ...data } : c));
    toast.success('Coupon updated ✅');
  };

  const handleToggle = async (coupon) => {
    const val = !coupon.isActive;
    await updateDoc(doc(db, 'coupons', coupon.id), { isActive: val });
    setCoupons((prev) => prev.map((c) => c.id === coupon.id ? { ...c, isActive: val } : c));
    toast.success(val ? 'Coupon activated' : 'Coupon deactivated');
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, 'coupons', id));
    setCoupons((prev) => prev.filter((c) => c.id !== id));
    toast.success('Coupon deleted');
  };

  const active   = coupons.filter((c) => c.isActive);
  const inactive = coupons.filter((c) => !c.isActive);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl text-gray-900">Coupons & Offers</h2>
          <p className="text-gray-400 font-body text-sm">
            {active.length} active · {coupons.length} total
          </p>
        </div>
        <button
          onClick={() => { setEditCoupon(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-5 py-2.5 rounded-2xl font-body font-semibold text-sm transition-all shadow-[0_4px_15px_rgba(244,63,94,0.3)]"
        >
          <Plus size={18} /> New Coupon
        </button>
      </div>

      {/* Quick templates */}
      <div className="grid sm:grid-cols-3 gap-3">
        {[
          { label: '🆕 New User 10% off',    code: 'WELCOME10', type: 'percent', value: 10, newUserOnly: true, autoApply: true,  desc: 'Auto 10% off for new users' },
          { label: '💝 Love ₹200 off',        code: 'LOVE200',   type: 'flat',    value: 200, minOrder: 999,    autoApply: false, desc: '₹200 off on orders above ₹999' },
          { label: '🎉 Big Sale 20% off',      code: 'SALE20',    type: 'percent', value: 20, minOrder: 499,    autoApply: false, desc: '20% off sitewide sale' },
        ].map((t) => (
          <button
            key={t.code}
            onClick={() => {
              setEditCoupon(null);
              setShowForm(true);
              // Pre-fill form by briefly delaying
              setTimeout(() => {}, 0);
            }}
            className="flex flex-col gap-1 text-left bg-white border border-gray-100 hover:border-rose-300 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all group"
          >
            <p className="font-body font-semibold text-gray-800 text-sm group-hover:text-rose-600 transition-colors">{t.label}</p>
            <p className="font-mono text-xs text-gray-400">{t.code}</p>
            <p className="text-xs text-gray-400 font-body">{t.desc}</p>
          </button>
        ))}
      </div>

      {/* Coupons list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-10 h-10 border-4 border-rose-100 border-t-rose-500 rounded-full animate-spin" />
        </div>
      ) : coupons.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <Tag size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-body font-medium mb-1">No coupons yet</p>
          <p className="text-gray-400 font-body text-sm">Create your first coupon to offer discounts to customers.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {active.length > 0 && (
            <>
              <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider px-1">
                Active ({active.length})
              </p>
              {active.map((c) => (
                <CouponRow key={c.id} coupon={c}
                  onEdit={(c) => { setEditCoupon(c); setShowForm(true); }}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                />
              ))}
            </>
          )}
          {inactive.length > 0 && (
            <>
              <p className="font-body font-semibold text-gray-600 text-xs uppercase tracking-wider px-1 mt-4">
                Inactive ({inactive.length})
              </p>
              {inactive.map((c) => (
                <CouponRow key={c.id} coupon={c}
                  onEdit={(c) => { setEditCoupon(c); setShowForm(true); }}
                  onToggle={handleToggle}
                  onDelete={handleDelete}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* Create/Edit modal */}
      {showForm && (
        <CouponForm
          initial={editCoupon}
          onSave={editCoupon ? handleEdit : handleCreate}
          onClose={() => { setShowForm(false); setEditCoupon(null); }}
        />
      )}
    </div>
  );
}
